import { Injectable } from '@angular/core';
import { ObjectDefinitionModel } from '@base/modules/rest/bbs-object/model/object-definition.model';
import { CustomFieldModel } from '@base/modules/rest/custom-field/model/custom-field.model';
import { ObjectRestServiceCreator } from '@base/modules/rest/object/object-rest-service-creator';
import { CustomFieldTypesEnum } from '@base/modules/rest/custom-field/enums/custom-field-types.enum';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { FilterOperationEnum } from '../../filter/enums/filter-operation.enum';

@Injectable()
export class FieldPathResolverService {

  private definitionCache = new Map<string, Observable<ObjectDefinitionModel | null>>();

  constructor(private objectRestServiceCreator: ObjectRestServiceCreator) {
  }

  resolveFieldPath(path: string | null | undefined, startDefinition: ObjectDefinitionModel): Observable<CustomFieldModel | null> {
    if (!path) {
      return of(null);
    }
    const segments = path.split('.');
    const currentDefinition$: Observable<ObjectDefinitionModel | null> = of(startDefinition);

    // Pipe through segments using reduce to chain observables
    return segments.reduce((accDef$, segment, index) => {
      return accDef$
        .pipe(
          switchMap(def => {
            if (!def || !def.fields) {
              return throwError(() => new Error(`Definition missing for segment ${segment} in path ${path}`)); // Throw error or return of(null)
            }

            let field = def.fields.find(f => f.name === segment);
            if (!field) {
              const segmentLower = segment.toLowerCase();
              field = def.fields.find(f => f.label.toLowerCase() === segmentLower);
            }

            if (!field) {
              return throwError(() => new Error(`Field ${segment} not found in ${def.name}`)); // Throw error or return of(null)
            }

            // Check if this is the last segment - if so, we are done resolving definition to find the field IN
            if (index === segments.length - 1) {
              return of(def); // Return the definition containing the last segment's field
            }

            // Not the last segment, check if it's a lookup to get the *next* definition
            if (field.type === CustomFieldTypesEnum.LOOKUP && field.lookupObject?.name) {
              return this.getCachedDefinition(field.lookupObject.name);
            } else {
              return throwError(() => new Error(`Field ${segment} is not a Lookup type in path ${path}`)); // Path broken
            }
          }),
          catchError(err => {
            console.error(`[FieldPathResolverService] Error during segment "${segment}" processing:`, err);
            return throwError(() => err); // Re-throw error
          })
        );
    }, currentDefinition$)
      .pipe(
        // After reducing/resolving the definition containing the final segment,
        // find the actual final field within that definition.
        map(finalDef => {
          if (!finalDef) {
            return null; // Should have been caught by throwError earlier
          }
          const lastSegment = segments[segments.length - 1];
          let finalField = finalDef.fields.find(f => f.name === lastSegment);
          if (!finalField) {
            const segmentLower = lastSegment.toLowerCase();
            finalField = finalDef.fields.find(f => f.label.toLowerCase() === segmentLower);
          }
          return finalField ?? null;
        }),
        catchError(err => {
          console.error(`[FieldPathResolverService] Error resolving final field for path '${path}':`, err);
          return of(null); // Return null if resolution fails
        })
      );
  }

  getCachedDefinition(objectName: string): Observable<ObjectDefinitionModel | null> {
    if (!this.definitionCache.has(objectName)) {
      const definition$ = this.objectRestServiceCreator.create(objectName).getDefinition()
        .pipe(
          catchError(err => {
            console.error(`[FieldPathResolverService] Error fetching definition for ${objectName}:`, err);
            // Decide how to handle: return null, throw error, etc. Returning null might hide issues.
            return of(null);
          }),
          shareReplay(1) // Cache the result
        );
      this.definitionCache.set(objectName, definition$);
    }
    // The ! assumes the key exists, which it does after the if/else block.
    return this.definitionCache.get(objectName)!;
  }

  // Simple helper to get operators based on type (can be expanded)
  getOperatorsForType(fieldType: CustomFieldTypesEnum | null): FilterOperationEnum[] {
    if (!fieldType) {
      return [];
    }

    switch (fieldType) {
      case CustomFieldTypesEnum.NUMBER:
      case CustomFieldTypesEnum.DECIMAL_NUMBER:
      case CustomFieldTypesEnum.PERCENT:
        return [
          FilterOperationEnum.HAS_VALUE,
          FilterOperationEnum.DOES_NOT_HAVE_VALUE,
          FilterOperationEnum.EQUALS,
          FilterOperationEnum.NOT_EQUALS,
          FilterOperationEnum.LESS_THAN,
          FilterOperationEnum.GREATER_THAN,
          FilterOperationEnum.LESS_THAN_OR_EQUAL,
          FilterOperationEnum.GREATER_THAN_OR_EQUAL,
          FilterOperationEnum.BETWEEN,
          FilterOperationEnum.NOT_BETWEEN,
        ];

      case CustomFieldTypesEnum.TEXT:
      case CustomFieldTypesEnum.TEXTAREA:
      case CustomFieldTypesEnum.EMAIL:
      case CustomFieldTypesEnum.URL:
      case CustomFieldTypesEnum.PHONE:
        return [
          FilterOperationEnum.HAS_VALUE,
          FilterOperationEnum.DOES_NOT_HAVE_VALUE,
          FilterOperationEnum.EQUALS,
          FilterOperationEnum.NOT_EQUALS,
          FilterOperationEnum.CONTAINS,
          FilterOperationEnum.NOT_IN,
          FilterOperationEnum.FROM,
          FilterOperationEnum.TO,
        ];

      case CustomFieldTypesEnum.DATE:
      case CustomFieldTypesEnum.DATE_PERIOD:
      case CustomFieldTypesEnum.TIME:
      case CustomFieldTypesEnum.DATE_TIME:
      case CustomFieldTypesEnum.DATE_TIME_PERIOD:
        return [
          FilterOperationEnum.HAS_VALUE,
          FilterOperationEnum.DOES_NOT_HAVE_VALUE,
          FilterOperationEnum.EQUALS,
          FilterOperationEnum.NOT_EQUALS,
          FilterOperationEnum.BETWEEN,
          FilterOperationEnum.NOT_BETWEEN,
          FilterOperationEnum.FROM,
          FilterOperationEnum.TO,
        ];

      case CustomFieldTypesEnum.MULTISELECT:
      case CustomFieldTypesEnum.RADIO_BUTTON:
      case CustomFieldTypesEnum.CHECKBOX:
      case CustomFieldTypesEnum.SWITCHER:
      case CustomFieldTypesEnum.LOOKUP:
      case CustomFieldTypesEnum.LOOKUP_ACTION:
        return [
          FilterOperationEnum.HAS_VALUE,
          FilterOperationEnum.DOES_NOT_HAVE_VALUE,
        ];
      case CustomFieldTypesEnum.NUMERATION:
      case CustomFieldTypesEnum.SELECT:
        return [
          FilterOperationEnum.HAS_VALUE,
          FilterOperationEnum.DOES_NOT_HAVE_VALUE,
          FilterOperationEnum.EQUALS,
          FilterOperationEnum.NOT_EQUALS,
        ];

      default:
        return [
          FilterOperationEnum.HAS_VALUE,
          FilterOperationEnum.DOES_NOT_HAVE_VALUE,
          FilterOperationEnum.EQUALS,
          FilterOperationEnum.NOT_EQUALS,
        ];
    }
  }
}
